import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Package, Truck, Shield, ExternalLink } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { getProxiedImageUrl, getProductPath } from '../utils/helpers';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import ShareButton from '../components/ShareButton';
import axios from 'axios';
import { trackViewItem } from '../lib/gads';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProductDetail = () => {
  const { sku } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [customQuantity, setCustomQuantity] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [relatedScroll, setRelatedScroll] = useState(0);
  const [compatBikes, setCompatBikes] = useState([]);

  useEffect(() => {
    // Scroll to top when product changes
    window.scrollTo(0, 0);
    loadProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sku]);

  // Load parsed bike compatibility whenever the product.sku is known
  useEffect(() => {
    if (!product?.sku) { setCompatBikes([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/bikes/compatibility/${encodeURIComponent(product.sku)}`);
        if (!cancelled) setCompatBikes(Array.isArray(r.data?.items) ? r.data.items : []);
      } catch {
        if (!cancelled) setCompatBikes([]);
      }
    })();
    return () => { cancelled = true; };
  }, [product?.sku]);

  // Google Ads remarketing: view_item event (fires once per product load)
  useEffect(() => {
    if (product?.sku) {
      trackViewItem(product);
    }
  }, [product?.sku]);

  const loadProduct = async () => {
    try {
      // Try by slug first, then by SKU
      let response;
      try {
        response = await axios.get(`${BACKEND_URL}/api/products/by-slug/${sku}`);
      } catch {
        response = await axios.get(`${BACKEND_URL}/api/products/${sku}`);
      }
      setProduct(response.data);

      // SEO: If user/bot landed on the SKU URL but the product has an SEO slug,
      // replace the URL in the browser with the canonical slug URL.
      // This eliminates duplicate URLs and resolves Google Search Console
      // "Duplicate page without user-selected canonical" warnings.
      const loadedProduct = response.data;
      if (loadedProduct.slug && loadedProduct.slug !== sku) {
        navigate(`/products/${loadedProduct.slug}`, { replace: true });
      }

      // Load related products using the actual SKU
      const productSku = loadedProduct.sku;
      axios.get(`${BACKEND_URL}/api/related-products?sku=${encodeURIComponent(productSku)}&limit=8`)
        .then(res => setRelatedProducts(res.data))
        .catch(() => {});
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="dark-container pt-20 min-h-screen flex items-center justify-center">
        <div className="text-2xl">Caricamento...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="dark-container pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="display-medium mb-6">Prodotto non trovato</h1>
          <Link to="/products" className="btn-primary">
            Torna ai Prodotti
          </Link>
        </div>
      </div>
    );
  }

  const displayName = language === 'it' ? product.name_it : product.name_en;
  const displayDescription = language === 'it' ? product.description_it : product.description_en;

  // SEO Meta Tags
  const productTitle = `${displayName} - ALLMX | ${product.category}`;
  const productDescription = displayDescription 
    ? displayDescription.substring(0, 155) + '...' 
    : `Acquista ${displayName} su ALLMX. ${product.brand ? `Marca: ${product.brand}.` : ''} Prezzo: €${product.price.toFixed(2)}. Spedizione veloce dall'Italia.`;
  const productKeywords = `${displayName}, ${product.category}, ${product.brand || 'motocross'}, ricambi moto, ALLMX, ${product.sku}`;
  const productImage = product.images && product.images.length > 0 
    ? product.images[0] 
    : 'https://allmx.it/og-image.jpg';
  const productUrl = `https://allmx.it${getProductPath(product)}`;

  // Sanitize SKU for Schema.org / Google Merchant: max 70 chars, no spaces or
  // slashes (Google flags "5042R/90" or "SU KIT400" as invalid).
  // We keep the original SKU in our DB; this is only for the JSON-LD output.
  const schemaSku = (product.sku || '')
    .replace(/[\s/\\?#&]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 70) || product.slug || 'ALLMX-PRODUCT';

  // Schema.org description: must be 50-5000 chars. Pad short ones with a
  // generated tail; cap long ones safely on a word boundary.
  const baseDesc = (displayDescription || '').trim();
  let schemaDescription;
  if (baseDesc.length >= 50 && baseDesc.length <= 5000) {
    schemaDescription = baseDesc;
  } else if (baseDesc.length > 5000) {
    schemaDescription = baseDesc.substring(0, 4990).replace(/\s+\S*$/, '') + '...';
  } else {
    // Too short or empty — build a sensible fallback
    const tail = `Acquista ${displayName} su ALLMX. ${product.brand ? `Marca ${product.brand}. ` : ''}Categoria: ${product.category}. Spedizione veloce dall'Italia.`;
    schemaDescription = (baseDesc ? baseDesc + ' ' : '') + tail;
    if (schemaDescription.length < 50) {
      schemaDescription = schemaDescription + ' Prodotto motocross premium per appassionati di off-road.';
    }
  }

  // Schema.org Product Markup (JSON-LD)
  const productSchema = {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": displayName,
    "description": schemaDescription,
    "sku": schemaSku,
    "brand": {
      "@type": "Brand",
      "name": product.brand || "ALLMX"
    },
    "image": product.images || [],
    "offers": {
      "@type": "Offer",
      "url": productUrl,
      "priceCurrency": "EUR",
      "price": product.price,
      "availability": product.stock > 0 
        ? "https://schema.org/InStock" 
        : (product.availability_status === 'preorder' 
          ? "https://schema.org/PreOrder" 
          : "https://schema.org/OutOfStock"),
      "seller": {
        "@type": "Organization",
        "name": "ALLMX - Ala S.r.l."
      },
      "shippingDetails": {
        "@type": "OfferShippingDetails",
        "shippingRate": {
          "@type": "MonetaryAmount",
          "value": "7.50",
          "currency": "EUR"
        },
        "shippingDestination": [
          {
            "@type": "DefinedRegion",
            "addressCountry": "IT"
          },
          {
            "@type": "DefinedRegion",
            "addressCountry": "ES"
          },
          {
            "@type": "DefinedRegion",
            "addressCountry": "FR"
          },
          {
            "@type": "DefinedRegion",
            "addressCountry": "DE"
          },
          {
            "@type": "DefinedRegion",
            "addressCountry": "NL"
          },
          {
            "@type": "DefinedRegion",
            "addressCountry": "BE"
          },
          {
            "@type": "DefinedRegion",
            "addressCountry": "AT"
          }
        ],
        "deliveryTime": {
          "@type": "ShippingDeliveryTime",
          "handlingTime": {
            "@type": "QuantitativeValue",
            "minValue": 1,
            "maxValue": 2,
            "unitCode": "DAY"
          },
          "transitTime": {
            "@type": "QuantitativeValue",
            "minValue": 2,
            "maxValue": 3,
            "unitCode": "DAY"
          }
        }
      },
      "hasMerchantReturnPolicy": {
        "@type": "MerchantReturnPolicy",
        "applicableCountry": ["IT", "ES", "FR", "DE", "NL", "BE", "AT"],
        "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
        "merchantReturnDays": 14,
        "returnMethod": "https://schema.org/ReturnByMail",
        "returnFees": "https://schema.org/ReturnShippingFees",
        "returnShippingFeesAmount": {
          "@type": "MonetaryAmount",
          "value": "7.50",
          "currency": "EUR"
        },
        "refundType": ["https://schema.org/FullRefund", "https://schema.org/ExchangeRefund"]
      }
    },
    "category": product.category
  };

  return (
    <div className="dark-container pt-20">
      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(productSchema)}
      </script>
      
      <SEO 
        title={productTitle}
        description={productDescription}
        keywords={productKeywords}
        image={productImage}
        url={productUrl}
        type="product"
        productPrice={product.price}
        productCurrency="EUR"
        productAvailability={product.stock > 0 ? 'in stock' : (product.availability_status === 'preorder' ? 'preorder' : 'out of stock')}
        productBrand={product.brand}
        productCategory={product.category}
        productSku={product.sku}
      />
      {/* Back Button */}
      <section className="py-6 px-6 lg:px-[7.6923%]">
        <button 
          onClick={() => navigate('/products')}
          className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
        >
          <ArrowLeft size={20} />
          {language === 'it' ? 'Torna ai Prodotti' : 'Back to Products'}
        </button>
      </section>

      {/* Product Detail */}
      <section className="py-12 px-6 lg:px-[7.6923%]">
        <div className="max-w-[1400px] mx-auto">
          {/* Breadcrumbs */}
          <Breadcrumbs 
            items={[
              { label: language === 'it' ? 'Prodotti' : 'Products', url: '/products' },
              { label: product.category, url: `/products?category=${encodeURIComponent(product.category)}` },
              { label: displayName }
            ]}
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Images */}
            <div>
              <div className="aspect-square bg-white/5 mb-4 overflow-hidden">
                {product.images && product.images.length > 0 ? (
                  <img
                    src={getProxiedImageUrl(product.images[selectedImage])}
                    alt={displayName}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/20">
                    {language === 'it' ? 'Immagine non disponibile' : 'No Image'}
                  </div>
                )}
              </div>
              {product.images && product.images.length > 1 && (
                <div className="grid grid-cols-4 gap-2">
                  {product.images.map((img, idx) => (
                    <div
                      key={`thumb-${idx}-${img.substring(img.lastIndexOf('/') + 1, img.lastIndexOf('/') + 15)}`}
                      onClick={() => setSelectedImage(idx)}
                      className={`aspect-square bg-white/5 cursor-pointer hover:opacity-80 transition-opacity overflow-hidden ${
                        selectedImage === idx ? 'ring-2 ring-[var(--brand-primary)]' : ''
                      }`}
                    >
                      <img 
                        src={getProxiedImageUrl(img)} 
                        alt={`${displayName} ${idx + 1}`} 
                        className="w-full h-full object-contain" 
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Info */}
            <div>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="text-sm" style={{ color: 'var(--brand-primary)' }}>
                  {product.category}
                </div>
                <ShareButton
                  product={product}
                  productName={displayName}
                  productPrice={product.price}
                />
              </div>
              <h1 className="display-medium mb-4">{displayName}</h1>
              
              {product.brand && (
                <div className="text-lg mb-4" style={{ color: 'var(--text-muted)' }}>
                  {language === 'it' ? 'Marca' : 'Brand'}: <span className="font-semibold text-white">{product.brand}</span>
                </div>
              )}

              <div className="text-5xl font-bold mb-6" style={{ color: 'var(--brand-primary)' }}>
                €{product.price ? product.price.toFixed(2) : '0.00'}
              </div>

              {/* Smart Badge: in_stock / preorder / out_of_stock */}
              {product.availability_status === 'preorder' ? (
                <div className="flex items-center gap-2 mb-6">
                  <Package size={20} className="text-orange-400" />
                  <span className="text-orange-400 font-semibold">
                    {language === 'it' ? `Disponibile su ordinazione (${product.stock} pz)` : `Available to order (${product.stock} pcs)`}
                  </span>
                </div>
              ) : product.stock > 0 ? (
                <div className="flex items-center gap-2 mb-6">
                  <Package size={20} className={product.stock < 10 ? 'text-orange-400' : 'text-green-400'} />
                  <span className={`font-semibold ${product.stock < 10 ? 'text-orange-400' : 'text-green-400'}`}>
                    {product.stock < 10 
                      ? (language === 'it' ? `Solo ${product.stock} disponibili!` : `Only ${product.stock} available!`)
                      : (language === 'it' ? `Disponibile (${product.stock} pz)` : `In Stock (${product.stock} pcs)`)
                    }
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2 mb-6">
                  <Package size={20} className="text-red-400" />
                  <span className="text-red-400 font-semibold">
                    {language === 'it' ? 'Non disponibile' : 'Out of Stock'}
                  </span>
                </div>
              )}

              {/* Smart Quantity Selector */}
              {product.stock > 0 && (
                <div className="mb-6">
                  {/* SKU Code */}
                  <div className="text-sm mb-3" style={{ color: 'var(--text-muted)' }}>
                    <span className="font-semibold">
                      {language === 'it' ? 'Codice Prodotto' : 'Product Code'}: 
                    </span>{' '}
                    <span className="font-mono" style={{ color: 'var(--text-primary)' }}>
                      {product.sku}
                    </span>
                  </div>
                  
                  <label className="block text-lg font-medium mb-2">
                    {language === 'it' ? 'Quantità' : 'Quantity'}
                  </label>
                  
                  {product.stock < 10 ? (
                    // Case 1-9: Simple dropdown with exact available quantity
                    <select
                      value={quantity}
                      onChange={(e) => setQuantity(parseInt(e.target.value))}
                      className="w-full h-12 bg-white/10 px-4 text-lg font-semibold hover:bg-white/20 transition-colors"
                      style={{ borderRadius: '0px' }}
                    >
                      {Array.from({ length: product.stock }, (_, i) => i + 1).map(num => (
                        <option key={num} value={num} style={{ background: '#1a1a1a', color: 'white' }}>
                          {num}
                        </option>
                      ))}
                    </select>
                  ) : (
                    // Case ≥10: Dropdown 1-9 + "10 o più" option
                    <div className="space-y-3">
                      <select
                        value={showCustomInput ? '10+' : quantity}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '10+') {
                            setShowCustomInput(true);
                            setCustomQuantity('');
                            setQuantity(10);
                          } else {
                            setShowCustomInput(false);
                            setQuantity(parseInt(val));
                          }
                        }}
                        className="w-full h-12 bg-white/10 px-4 text-lg font-semibold hover:bg-white/20 transition-colors"
                        style={{ borderRadius: '0px' }}
                      >
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                          <option key={num} value={num} style={{ background: '#1a1a1a', color: 'white' }}>
                            {num}
                          </option>
                        ))}
                        <option value="10+" style={{ background: '#1a1a1a', color: 'white' }}>
                          {language === 'it' ? '10 o più' : '10 or more'}
                        </option>
                      </select>
                      
                      {showCustomInput && (
                        <div className="space-y-2">
                          <label className="text-sm text-gray-400">
                            {language === 'it' ? `Inserisci quantità (max ${product.stock})` : `Enter quantity (max ${product.stock})`}
                          </label>
                          <input
                            type="number"
                            min="10"
                            max={product.stock}
                            value={customQuantity}
                            onChange={(e) => {
                              const val = parseInt(e.target.value) || 10;
                              const clamped = Math.min(Math.max(10, val), product.stock);
                              setCustomQuantity(e.target.value);
                              setQuantity(clamped);
                            }}
                            placeholder="10"
                            className="w-full h-12 bg-white/10 px-4 text-lg font-semibold"
                            style={{ borderRadius: '0px' }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Add to Cart */}
              <button 
                className={`w-full mb-4 text-xl py-4 flex items-center justify-center gap-3 rounded font-semibold transition-all ${
                  product.stock === 0 
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                    : 'btn-primary'
                }`}
                disabled={product.stock === 0}
                onClick={() => product.stock > 0 && addToCart(product, quantity)}
              >
                <ShoppingCart size={24} />
                {product.stock === 0 
                  ? (language === 'it' ? 'Prodotto Non Disponibile' : 'Product Unavailable')
                  : (language === 'it' ? 'Aggiungi al Carrello' : 'Add to Cart')
                }
              </button>
              
              {/* Avviso prodotto esaurito */}
              {product.stock === 0 && (
                <div className="bg-red-500/10 border border-red-500/30 rounded p-4 mb-4">
                  <p className="text-red-400 font-semibold text-center">
                    {language === 'it'
                      ? '⚠️ Questo prodotto è temporaneamente esaurito.'
                      : language === 'es'
                      ? '⚠️ Este producto está temporalmente agotado.'
                      : '⚠️ This product is temporarily out of stock.'}
                  </p>
                </div>
              )}

              {/* Trust Badges */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="dark-card p-3 text-center">
                  <Truck size={24} className="mx-auto mb-2" style={{ color: 'var(--brand-primary)' }} />
                  <div className="text-xs">{language === 'it' ? 'Spedizione Veloce' : 'Fast Shipping'}</div>
                </div>
                <div className="dark-card p-3 text-center">
                  <Shield size={24} className="mx-auto mb-2" style={{ color: 'var(--brand-primary)' }} />
                  <div className="text-xs">{language === 'it' ? 'Garanzia' : 'Warranty'}</div>
                </div>
                <div className="dark-card p-3 text-center">
                  <Package size={24} className="mx-auto mb-2" style={{ color: 'var(--brand-primary)' }} />
                  <div className="text-xs">{language === 'it' ? 'Stock Reale' : 'Real Stock'}</div>
                </div>
              </div>

              {/* Also on Marketplaces */}
              <div className="dark-card p-4">
                <div className="text-sm mb-3 font-semibold" style={{ color: 'var(--text-primary)' }}>
                  {language === 'it' ? 'Disponibile anche su:' : 'Also available on:'}
                </div>
                <div className="flex gap-3">
                  <a
                    href="https://www.amazon.it/s?me=A3526ZZLQNVU1W&marketplaceID=APJ6JRA9NG5V4"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 text-sm py-3 px-4 rounded font-bold transition-all flex items-center justify-center gap-2"
                    style={{
                      backgroundColor: 'var(--bg-tertiary)',
                      color: 'var(--text-primary)',
                      border: '2px solid var(--border-primary)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = 'var(--brand-primary)';
                      e.target.style.color = '#000000';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'var(--bg-tertiary)';
                      e.target.style.color = 'var(--text-primary)';
                    }}
                  >
                    Amazon <ExternalLink size={16} />
                  </a>
                  <a
                    href="https://www.ebay.it/str/allmx"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 text-sm py-3 px-4 rounded font-bold transition-all flex items-center justify-center gap-2"
                    style={{
                      backgroundColor: 'var(--bg-tertiary)',
                      color: 'var(--text-primary)',
                      border: '2px solid var(--border-primary)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = 'var(--brand-primary)';
                      e.target.style.color = '#000000';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'var(--bg-tertiary)';
                      e.target.style.color = 'var(--text-primary)';
                    }}
                  >
                    eBay <ExternalLink size={16} />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="mt-12">
            <h2 className="display-medium mb-6">
              {language === 'it' ? 'Descrizione' : 'Description'}
            </h2>
            <div className="body-large whitespace-pre-line" style={{ color: 'var(--text-secondary)' }}>
              <div style={{ wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'pre-wrap' }}>
                {displayDescription}
              </div>
            </div>
          </div>

          {/* Compatibilità Moto — placed right under the "Descrizione" H2 */}
          {(compatBikes.length > 0 ||
            (Array.isArray(product.compatible_models) && product.compatible_models.length > 0)) && (
            <div className="mt-10" data-testid="compatible-models-section">
              <h2 className="display-medium mb-4">
                {language === 'it' ? 'Compatibilità Moto' : language === 'es' ? 'Compatibilidad Moto' : 'Bike Compatibility'}
              </h2>
              <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
                {language === 'it'
                  ? 'Clicca una moto per vedere tutti i ricambi compatibili:'
                  : language === 'es'
                  ? 'Haz clic en una moto para ver todos los recambios compatibles:'
                  : 'Click a bike to see all compatible parts:'}
              </p>
              <div className="flex flex-wrap gap-2">
                {compatBikes.length > 0 ? (
                  // Parsed chips -> each links to the Bike Finder with brand/model/year prefilled
                  compatBikes.map((b, idx) => {
                    const label = [
                      b.brand,
                      b.model,
                      b.year_from && b.year_to && b.year_from !== b.year_to
                        ? `${b.year_from} - ${b.year_to}`
                        : b.year_from || null,
                    ]
                      .filter(Boolean)
                      .join(' ');
                    if (!b.brand) {
                      return (
                        <span
                          key={idx}
                          className="inline-block px-3 py-1.5 text-sm border"
                          style={{
                            background: 'var(--bg-secondary)',
                            borderColor: 'var(--text-muted)',
                            color: 'var(--text-muted)',
                          }}
                          data-testid={`compatible-model-${idx}`}
                        >
                          {b.raw || label}
                        </span>
                      );
                    }
                    // Link to BikeFinder with the exact year (first year of range) selected
                    const linkParams = new URLSearchParams();
                    linkParams.set('brand', b.brand);
                    if (b.year_from) linkParams.set('year', String(b.year_from));
                    if (b.model) linkParams.set('model', b.model);
                    const path =
                      language === 'es'
                        ? '/buscar-recambios-por-moto'
                        : language === 'en'
                        ? '/find-parts-by-bike'
                        : '/trova-ricambi-per-moto';
                    return (
                      <Link
                        key={idx}
                        to={`${path}?${linkParams.toString()}`}
                        className="inline-block px-3 py-1.5 text-sm border transition-all hover:scale-105 hover:shadow-lg"
                        style={{
                          background: 'var(--bg-secondary)',
                          borderColor: 'var(--brand-primary)',
                          color: 'var(--text-primary)',
                        }}
                        data-testid={`compatible-model-${idx}`}
                        title={language === 'it' ? 'Vedi tutti i ricambi per questa moto' : 'See all parts for this bike'}
                      >
                        {label}
                      </Link>
                    );
                  })
                ) : (
                  // Fallback: show raw strings if the parsed data is not available yet
                  product.compatible_models.map((bike, idx) => (
                    <span
                      key={idx}
                      className="inline-block px-3 py-1.5 text-sm border"
                      style={{
                        background: 'var(--bg-secondary)',
                        borderColor: 'var(--brand-primary)',
                        color: 'var(--text-primary)',
                      }}
                      data-testid={`compatible-model-${idx}`}
                    >
                      {bike}
                    </span>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Additional Info */}
          {(product.mpn || product.ean || product.model) && (
            <div className="mt-12 dark-card p-6">
              <h3 className="text-xl font-semibold mb-4">
                {language === 'it' ? 'Informazioni Tecniche' : 'Technical Information'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {product.sku && (
                  <div>
                    <div className="text-sm" style={{ color: 'var(--text-muted)' }}>SKU</div>
                    <div className="font-semibold">{product.sku}</div>
                  </div>
                )}
                {product.mpn && (
                  <div>
                    <div className="text-sm" style={{ color: 'var(--text-muted)' }}>MPN</div>
                    <div className="font-semibold">{product.mpn}</div>
                  </div>
                )}
                {product.ean && (
                  <div>
                    <div className="text-sm" style={{ color: 'var(--text-muted)' }}>EAN</div>
                    <div className="font-semibold">{product.ean}</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Modal Avviso Disponibilità */}
      {/* Related Products Carousel */}
      {relatedProducts.length > 0 && (
        <section className="py-12 px-6 lg:px-[7.6923%]" style={{ backgroundColor: 'var(--bg-secondary)' }}>
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold">
                {language === 'it' ? 'Prodotti Correlati' : 'Related Products'}
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const el = document.getElementById('related-scroll');
                    if (el) el.scrollBy({ left: -280, behavior: 'smooth' });
                  }}
                  className="w-9 h-9 flex items-center justify-center bg-white/10 hover:bg-[var(--brand-primary)] hover:text-black rounded-full transition-colors"
                  data-testid="related-prev"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
                </button>
                <button
                  onClick={() => {
                    const el = document.getElementById('related-scroll');
                    if (el) el.scrollBy({ left: 280, behavior: 'smooth' });
                  }}
                  className="w-9 h-9 flex items-center justify-center bg-white/10 hover:bg-[var(--brand-primary)] hover:text-black rounded-full transition-colors"
                  data-testid="related-next"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
                </button>
              </div>
            </div>
            <div
              id="related-scroll"
              className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {relatedProducts.map((rp) => (
                <Link
                  key={rp.sku}
                  to={getProductPath(rp)}
                  className="flex-shrink-0 w-[220px] md:w-[260px] group"
                  data-testid={`related-${rp.sku}`}
                >
                  <div className="dark-card overflow-hidden transition-all hover:border-[var(--brand-primary)]/50">
                    <div className="aspect-square bg-white/5 relative overflow-hidden">
                      {rp.images && rp.images.length > 0 ? (
                        <img
                          src={getProxiedImageUrl(rp.images[0])}
                          alt={rp.name_it}
                          className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white/20 text-xs">No Image</div>
                      )}
                    </div>
                    <div className="p-3">
                      <p className="text-xs mb-1" style={{ color: 'var(--brand-primary)' }}>{rp.brand || rp.category}</p>
                      <h3 className="text-sm font-semibold line-clamp-2 mb-2 group-hover:text-[var(--brand-primary)] transition-colors" style={{ minHeight: '2.5rem' }}>
                        {rp.name_it}
                      </h3>
                      <p className="text-lg font-bold" style={{ color: 'var(--brand-primary)' }}>
                        €{rp.price?.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

    </div>
  );
};

export default ProductDetail;

